import { useCustomerPricingTierContext } from '@src/providers/CustomerPricingTiersProvider';
import {
  IconName,
  useEffectOnce,
  type AllInTier,
  type FeeTier,
  type FilterableProperty,
  type SymbolGroup,
} from '@talos/kyoko';
import { useMemo, useState } from 'react';

interface UsePricingTierFiltersOptions {
  allInTierField?: string;
  feeTierField?: string;
  symbolGroupField?: string;
  preserveSelectionsWhileLoading?: boolean;
}

interface PricingTierFilters {
  allInTierFilter: FilterableProperty<'AllInTiers'> | null;
  feeTierFilter: FilterableProperty<'FeeTiers'> | null;
  symbolGroupFilter: FilterableProperty<'SymbolGroups'> | null;
}

interface LoadingSafePricingTierFilters {
  allInTierFilter: FilterableProperty<'AllInTiers'>;
  feeTierFilter: FilterableProperty<'FeeTiers'>;
  symbolGroupFilter: FilterableProperty<'SymbolGroups'>;
}

function getLoadingPricingTierFilter<TKey extends 'AllInTiers' | 'FeeTiers' | 'SymbolGroups'>({
  field,
  key,
  label,
}: {
  field: string;
  key: TKey;
  label: string;
}): FilterableProperty<TKey> {
  return {
    key,
    label,
    field,
    icon: IconName.Cube,
    options: [],
    getOptionLabel: option => option,
    clearUnsupportedSelectionsOnInit: false,
  };
}

export function usePricingTierFilters(
  options: UsePricingTierFiltersOptions & { preserveSelectionsWhileLoading: true }
): LoadingSafePricingTierFilters;
export function usePricingTierFilters(options?: UsePricingTierFiltersOptions): PricingTierFilters;
export function usePricingTierFilters(options?: UsePricingTierFiltersOptions): PricingTierFilters {
  const { getAllInTiers, getFeeTiers, getSymbolGroups } = useCustomerPricingTierContext();

  const [allInTiers, setAllInTiers] = useState<AllInTier[]>();
  const [feeTiers, setFeeTiers] = useState<FeeTier[]>();
  const [symbolGroups, setSymbolGroups] = useState<SymbolGroup[]>();

  const [allInTierOptions, setAllInTierOptions] = useState<string[]>();
  const [feeTierOptions, setFeeTierOptions] = useState<string[]>();
  const [symbolGroupOptions, setSymbolGroupOptions] = useState<string[]>();

  useEffectOnce(() => {
    getAllInTiers().then(({ data }) => {
      setAllInTiers(data);
      setAllInTierOptions(data.map(tier => tier.Tier));
    });
    getFeeTiers().then(({ data }) => {
      setFeeTiers(data);
      setFeeTierOptions(data.map(tier => tier.Tier));
    });
    getSymbolGroups().then(({ data }) => {
      setSymbolGroups(data);
      setSymbolGroupOptions(data.map(tier => tier.Tier));
    });
  });

  const allInTierFilter: FilterableProperty<'AllInTiers'> | null = useMemo(
    () =>
      allInTiers && allInTierOptions
        ? {
            key: 'AllInTiers',
            label: 'Base Tier',
            field: options?.allInTierField ?? 'AllInTier',
            icon: IconName.Cube,
            options: allInTierOptions,
            getOptionLabel: option => allInTiers.find(tier => tier.Tier === option)?.Tier ?? option,
          }
        : options?.preserveSelectionsWhileLoading
          ? getLoadingPricingTierFilter({
              key: 'AllInTiers',
              label: 'Base Tier',
              field: options.allInTierField ?? 'AllInTier',
            })
          : null,
    [allInTierOptions, allInTiers, options?.allInTierField, options?.preserveSelectionsWhileLoading]
  );

  const feeTierFilter: FilterableProperty<'FeeTiers'> | null = useMemo(
    () =>
      feeTiers && feeTierOptions
        ? {
            key: 'FeeTiers',
            label: 'Fee Tier',
            field: options?.feeTierField ?? 'FeeTier',
            icon: IconName.Cube,
            options: feeTierOptions,
            getOptionLabel: option => feeTiers.find(tier => tier.Tier === option)?.Tier ?? option,
          }
        : options?.preserveSelectionsWhileLoading
          ? getLoadingPricingTierFilter({
              key: 'FeeTiers',
              label: 'Fee Tier',
              field: options.feeTierField ?? 'FeeTier',
            })
          : null,
    [feeTierOptions, feeTiers, options?.feeTierField, options?.preserveSelectionsWhileLoading]
  );

  const symbolGroupFilter: FilterableProperty<'SymbolGroups'> | null = useMemo(
    () =>
      symbolGroups && symbolGroupOptions
        ? {
            key: 'SymbolGroups',
            label: 'Symbol Group',
            field: options?.symbolGroupField ?? 'SymbolGroup',
            icon: IconName.Cube,
            options: symbolGroupOptions,
            getOptionLabel: option => symbolGroups.find(tier => tier.Tier === option)?.Tier ?? option,
          }
        : options?.preserveSelectionsWhileLoading
          ? getLoadingPricingTierFilter({
              key: 'SymbolGroups',
              label: 'Symbol Group',
              field: options.symbolGroupField ?? 'SymbolGroup',
            })
          : null,
    [options?.preserveSelectionsWhileLoading, options?.symbolGroupField, symbolGroupOptions, symbolGroups]
  );

  return useMemo(
    () => ({ allInTierFilter, feeTierFilter, symbolGroupFilter }),
    [allInTierFilter, feeTierFilter, symbolGroupFilter]
  );
}
